import {
  AnimatedModal,
  EHeadingStyleVariant,
  EModalSize,
  EModalVariants,
  ETextStyleVariant,
  GeneralCompassIcon,
  GeneralMessageMessageSquareIcon,
  GeneralStarOutlineIcon,
  Heading,
  Modal,
  RawButton,
  Text,
} from '@outdoorsyco/bonfire';
import { FormattedMessage, useIntl } from 'react-intl';

import { useBreakpointContext } from '@/components/common/BreakpointContext';
import { openZendeskChat } from '@/components/ZendeskChat/ZendeskChat';
import { EFeedbackEventSource, trackFeedbackCTASelectedEvent } from '@/services/analytics/feedback';
import { parsePageMetaData } from '@/services/analytics/pageEventUtils';

type TGetHelpModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const GetHelpModal = ({ isOpen, onClose }: TGetHelpModalProps) => {
  const { isAboveDesktop } = useBreakpointContext();

  const content = <GetHelpContent onClose={onClose} />;

  if (isAboveDesktop) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size={EModalSize.Small} withOverlay isDismissable>
        {content}
      </Modal>
    );
  }

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onClose}
      variant={EModalVariants.Bottom}
      withOverlay
      isDismissable>
      {content}
    </AnimatedModal>
  );
};

type TGetHelpContentProps = {
  onClose: () => void;
};

const GetHelpContent = ({ onClose }: TGetHelpContentProps) => {
  const intl = useIntl();

  return (
    <>
      <Modal.Actions />

      <Modal.Header>
        <Heading component="div" variant={EHeadingStyleVariant.H6}>
          <FormattedMessage
            id="iq1OIA"
            defaultMessage="Get help"
            description="GlobalHeader > Get help modal > Title"
          />
        </Heading>
      </Modal.Header>

      <Modal.Content className="flex flex-col">
        <div className="flex flex-col py-1">
          <GetHelpOption
            icon={<GeneralMessageMessageSquareIcon />}
            label={intl.formatMessage({
              id: 'E14R0J',
              defaultMessage: 'Contact Us',
              description: 'GlobalHeader > Get help modal > Contact Us option',
            })}
            onClick={() => {
              onClose();
              openZendeskChat();
            }}
          />

          <GetHelpOption
            icon={<GeneralCompassIcon />}
            label={intl.formatMessage({
              id: 'vBem2+',
              defaultMessage: 'Visit the help center',
              description: 'GlobalHeader > Get help modal > Visit the help center option',
            })}
            onClick={() => {
              onClose();
              window.open('/help', '_blank');
            }}
          />

          <GetHelpOption
            icon={<GeneralStarOutlineIcon />}
            label={intl.formatMessage({
              id: 'q+SNDE',
              defaultMessage: 'Give us feedback',
              description: 'GlobalHeader > Get help modal > Give us feedback option',
            })}
            onClick={() => {
              onClose();
              trackFeedbackCTASelectedEvent({
                pageCategory: parsePageMetaData(window.sessionStorage).pageCategory,
                source: EFeedbackEventSource.HELP_MENU,
              });
            }}
          />
        </div>
      </Modal.Content>
    </>
  );
};

type TGetHelpOptionProps = {
  icon: JSX.Element;
  label: string;
  onClick: () => void;
};

const GetHelpOption = ({ icon, label, onClick }: TGetHelpOptionProps) => (
  <RawButton className="relative block w-full" onPress={onClick}>
    <div className="flex items-center justify-between w-full px-2 py-3 gap-4">
      <Text
        component="div"
        variant={ETextStyleVariant.MediumRegular}
        className="flex items-center gap-3">
        <span className="text-secondary-base" aria-hidden>
          {icon}
        </span>
        {label}
      </Text>
    </div>
  </RawButton>
);
